import { useEffect, useState } from 'react';
import { isPdf } from '@/features/magic/attachment.lib';
import type { Attachment } from '@/features/magic/attachment.types';
import { toImage } from '@/features/magic/pdf.lib';
import { Icon, IconButton, Spinner } from '@/ui';
import { AttachmentThumbnailContainer } from './AttachmentThumbnailContainer';

type Props = {
  attachment: Attachment;
  onRemove: () => void;
};

const AttachmentThumbnail = ({ attachment, onRemove }: Props) => {
  const [url, setUrl] = useState<string | null>(
    attachment.contentType?.startsWith('image') ? attachment.url : null
  );
  const [isLoading, setLoading] = useState(!url);

  useEffect(() => {
    (async () => {
      if (isPdf(attachment)) {
        try {
          const asImage = await toImage(attachment.url);
          setUrl(asImage);
        } catch {
          // A corrupt or empty PDF (e.g. a zero-byte upload) makes pdf.js reject
          // with InvalidPDFException. Without this guard it escaped as an
          // unhandled rejection and left the spinner running forever (Sentry
          // WEB-CLIENT-26F). Fall back to the placeholder thumbnail instead.
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [attachment]);

  return (
    <AttachmentThumbnailContainer
      bg={url ? `url('${url}')` : 'gray.200'}
      data-group
    >
      {isLoading && <Spinner />}

      <IconButton
        aria-label="Remove attachment"
        borderRadius="full"
        colorScheme="dark"
        icon={<Icon icon="X" />}
        opacity={0}
        pos="absolute"
        right="0"
        size="xs"
        top="0"
        transform="translate(33%, -33%)"
        transition="opacity 0.2s ease"
        variant="solid"
        _groupHover={{
          opacity: 1,
        }}
        onClick={onRemove}
      />
    </AttachmentThumbnailContainer>
  );
};

export default AttachmentThumbnail;
